/**
 * HOWBODY Body Composition Analysis Report — field definitions.
 *
 * Pure data. Lives outside `@/lib/LongevityEngine` (locked Engine file).
 * Shared by the upload/review dialog, the report view and the trend charts
 * so a label is only ever written once.
 */

export const SUMMARY_FIELDS = Object.freeze([
  { key: "weight_kg", label: "Weight", unit: "kg" },
  { key: "body_fat_rate_percent", label: "Body Fat Rate", unit: "%" },
  { key: "basal_metabolism_kcal", label: "Basal Metabolism", unit: "kcal" },
  { key: "metabolic_age_years", label: "Metabolic Age", unit: "years" },
  { key: "target_weight_kg", label: "Target Weight", unit: "kg" },
  { key: "weight_control_kg", label: "Weight Control", unit: "kg" },
  { key: "muscle_control_kg", label: "Muscle Control", unit: "kg" },
  { key: "fat_control_kg", label: "Fat Control", unit: "kg" },
]);

export const COMPOSITION_FIELDS = Object.freeze([
  { key: "weight_kg", label: "Weight", unit: "kg" },
  { key: "total_water_kg", label: "Total Water", unit: "kg" },
  { key: "protein_kg", label: "Protein", unit: "kg" },
  { key: "minerals_kg", label: "Minerals", unit: "kg" },
  { key: "intracellular_fluid_l", label: "Intracellular fluid", unit: "L" },
  { key: "extracellular_fluid_l", label: "Extracellular fluid", unit: "L" },
]);

export const MUSCLE_FAT_FIELDS = Object.freeze([
  { key: "skeletal_muscle_kg", label: "Skeletal Muscle", unit: "kg" },
  { key: "body_fat_kg", label: "Body Fat", unit: "kg" },
  { key: "fat_free_mass_kg", label: "Fat-Free Mass", unit: "kg" },
]);

export const BODY_MASS_FIELDS = Object.freeze([
  { key: "bmi", label: "BMI", unit: "kg/m²" },
  { key: "body_fat_percentage", label: "Body Fat Percentage", unit: "%" },
  { key: "waist_hip_ratio", label: "Waist-Hip Ratio", unit: "" },
  { key: "visceral_fat_level", label: "Visceral Fat Level", unit: "" },
]);

export const SEGMENTS = Object.freeze([
  { key: "left_arm", label: "Left Arm" },
  { key: "right_arm", label: "Right Arm" },
  { key: "trunk", label: "Trunk" },
  { key: "left_leg", label: "Left Leg" },
  { key: "right_leg", label: "Right Leg" },
]);

export const STATUS_OPTIONS = Object.freeze(["Low", "Standard", "Above Standard"]);

/** Every numeric key the review screen can edit, in display order. */
export const ALL_NUMERIC_FIELDS = Object.freeze([
  { key: "comprehensive_score", label: "Comprehensive Score", unit: "/ 100" },
  ...SUMMARY_FIELDS,
  ...COMPOSITION_FIELDS.filter((f) => f.key !== "weight_kg"),
  ...MUSCLE_FAT_FIELDS,
  ...BODY_MASS_FIELDS,
]);

export const TREND_METRICS = Object.freeze([
  { key: "comprehensive_score", label: "Comprehensive Score", unit: "", color: "#2e8b63" },
  { key: "weight_kg", label: "Weight", unit: "kg", color: "#4a7fa6" },
  { key: "body_fat_rate_percent", label: "Body Fat Rate", unit: "%", color: "#b4474d" },
  { key: "skeletal_muscle_kg", label: "Skeletal Muscle", unit: "kg", color: "#c07b36" },
  { key: "bmi", label: "BMI", unit: "kg/m²", color: "#6c5ea8" },
  { key: "visceral_fat_level", label: "Visceral Fat Level", unit: "", color: "#b67358" },
  { key: "basal_metabolism_kcal", label: "Basal Metabolism", unit: "kcal", color: "#5d8a5a" },
  { key: "metabolic_age_years", label: "Metabolic Age", unit: "years", color: "#4b5f8c" },
]);

export const STATUS_TONE = Object.freeze({
  Low: { fg: "#8a5a1c", bg: "#fae3cc" },
  Standard: { fg: "#1f6b4d", bg: "#dff0e6" },
  "Above Standard": { fg: "#8f2f2f", bg: "#f8dada" },
});

export const numberOrNull = (raw) => {
  if (raw === null || raw === undefined || raw === "") return null;
  const n = Number(String(raw).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

export const fmtValue = (v, digits = 1) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return Number.isInteger(n) ? String(n) : n.toFixed(digits);
};

export const fmtRange = (range) => {
  if (!range) return "";
  const { low, high } = range;
  if (low === null && high === null) return "";
  if (low !== null && high !== null) return `${low}~${high}`;
  return low !== null ? `≥ ${low}` : `≤ ${high}`;
};

/** "2026-10-19 12:47:27" → "19 Oct 2026 · 12:47" */
export const fmtTestTime = (raw, withTime = true) => {
  if (!raw) return "—";
  const iso = String(raw).replace(" ", "T");
  const d = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return String(raw);
  const date = d.toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric", timeZone: "UTC",
  });
  if (!withTime) return date;
  const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "UTC" });
  return `${date} · ${time}`;
};

export const shortTestDate = (raw) => fmtTestTime(raw, false);

/** Reads a metric off a stored report document. */
export const reportValue = (report, key) => {
  const v = report?.values?.[key];
  return v === undefined ? null : v;
};
